import type { Quote, QuoteField } from '../../data/protocol/types';
import { formatCompact, formatNumber, formatSigned, formatTime } from '../../shared/format';

export interface QuoteColumn {
  field: QuoteField;
  label: string;
  widthPx: number;
  align?: 'left' | 'right';
  value: (quote: Quote) => string;
  tone?: (quote: Quote) => 'positive' | 'negative' | 'neutral';
}

export const QUOTE_COLUMNS: Record<QuoteField, QuoteColumn> = {
  symbol: { field: 'symbol', label: 'Symbol', widthPx: 110, align: 'left', value: (q) => q.symbol },
  last: { field: 'last', label: 'Last', widthPx: 96, align: 'right', value: (q) => formatNumber(q.last), tone: directionTone },
  bid: { field: 'bid', label: 'Bid', widthPx: 92, align: 'right', value: (q) => formatNumber(q.bid) },
  ask: { field: 'ask', label: 'Ask', widthPx: 92, align: 'right', value: (q) => formatNumber(q.ask) },
  bidSize: { field: 'bidSize', label: 'Bid Sz', widthPx: 78, align: 'right', value: (q) => formatCompact(q.bidSize) },
  askSize: { field: 'askSize', label: 'Ask Sz', widthPx: 78, align: 'right', value: (q) => formatCompact(q.askSize) },
  change: { field: 'change', label: 'Chg', widthPx: 84, align: 'right', value: (q) => formatSigned(q.change), tone: changeTone },
  changePct: { field: 'changePct', label: 'Chg %', widthPx: 88, align: 'right', value: (q) => `${formatSigned(q.changePct)}%`, tone: changeTone },
  volume: { field: 'volume', label: 'Volume', widthPx: 104, align: 'right', value: (q) => formatCompact(q.volume) },
  high: { field: 'high', label: 'High', widthPx: 92, align: 'right', value: (q) => formatNumber(q.high) },
  low: { field: 'low', label: 'Low', widthPx: 92, align: 'right', value: (q) => formatNumber(q.low) },
  open: { field: 'open', label: 'Open', widthPx: 92, align: 'right', value: (q) => formatNumber(q.open) },
  exchange: { field: 'exchange', label: 'Exch', widthPx: 78, align: 'left', value: (q) => q.exchange },
  time: { field: 'time', label: 'Time', widthPx: 104, align: 'right', value: (q) => q.time ? formatTime(q.time) : '—' }
};

function directionTone(quote: Quote): 'positive' | 'negative' | 'neutral' {
  if (quote.direction > 0) return 'positive';
  if (quote.direction < 0) return 'negative';
  return 'neutral';
}

function changeTone(quote: Quote): 'positive' | 'negative' | 'neutral' {
  if (quote.change > 0) return 'positive';
  if (quote.change < 0) return 'negative';
  return 'neutral';
}
